import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  Skeleton,
  Tab,
  Tabs,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ManageAccounts as ManageAccountsIcon,
  CreditCard as CreditCardIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  AutoAwesome as AutoAwesomeIcon,
  FactCheck as FactCheckIcon,
  Inventory2 as Inventory2Icon,
  Language as LanguageIcon,
  Storefront as StorefrontIcon,
  WhatsApp as WhatsAppIcon,
  Security as SecurityIcon,
  Extension as ExtensionIcon,
  Download as DownloadIcon,
  ReceiptLong as ReceiptLongIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import DashboardHeader from '../common/DashboardHeader';
import { isMobileShellLayout } from '../../utils/mobileShellLayout';
import LandingUsers from './LandingUsers';
import SubscriptionBillingDrawer from './SubscriptionBillingDrawer';
import axios from 'axios';
import { loadRazorpayScript } from '../../nexastay/nexastayPayment';
import {
  buildAddonsPayload,
  buildCouponContextSignature,
  buildOrderCouponFields,
  createEmptyCouponDetails,
  fetchWebsiteSubscriptionQuote,
  normalizeCouponCode,
  resolveCheckoutDisplayTotal,
  resolveCheckoutSavingsMajor,
  toApiBillingInterval,
  validateWebsiteSubscriptionCoupon,
} from '../../utils/websiteSubscriptionPayments';

const getSubscriptionCheckoutContact = (hotel = {}) => {
  const djangoContext = typeof window !== 'undefined' ? (window.DJANGO_CONTEXT || {}) : {};
  const user = djangoContext.user || {};
  const customerName = String(user.fullName || user.username || '').trim();
  const customerEmail = String(user.email || '').trim().toLowerCase();
  const customerPhone = String(
    hotel.contact_phone
    || djangoContext.selectedHotel?.contact_phone
    || djangoContext.selectedHotel?.whatsapp_number
    || ''
  ).trim();
  return { customerName, customerEmail, customerPhone, djangoContext, user };
};

const tabA11yProps = (tabKey) => ({
  id: `settings-tab-${tabKey}`,
  'aria-controls': `settings-panel-${tabKey}`,
});

const resolveInitialSettingsTab = (requestedTab, canManageUsers, canManageSubscription) => {
  if (requestedTab === 'subscription' && canManageSubscription) return 'subscription';
  if (requestedTab === 'users' && canManageUsers) return 'users';
  if (canManageUsers) return 'users';
  return canManageSubscription ? 'subscription' : 'users';
};

const formatDateTime = (value, fallback = 'No end date') => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleString();
};

const statusTone = (status, isDark) => {
  if (status === 'not_configured') {
    return {
      background: isDark ? alpha('#94a3b8', 0.22) : alpha('#64748b', 0.1),
      border: isDark ? alpha('#94a3b8', 0.42) : alpha('#64748b', 0.26),
      color: isDark ? '#cbd5e1' : '#475569',
      icon: <AccessTimeIcon sx={{ fontSize: 14 }} />,
    };
  }
  if (status === 'expired') {
    return {
      background: isDark ? alpha('#ff4861', 0.22) : alpha('#ff4861', 0.12),
      border: isDark ? alpha('#ff4861', 0.48) : alpha('#ff4861', 0.3),
      color: '#ff4861',
      icon: <ErrorOutlineIcon sx={{ fontSize: 14 }} />,
    };
  }
  if (status === 'upcoming') {
    return {
      background: isDark ? alpha('#f6da6e', 0.22) : alpha('#f6da6e', 0.12),
      border: isDark ? alpha('#f6da6e', 0.5) : alpha('#f6da6e', 0.35),
      color: '#f6da6e',
      icon: <AccessTimeIcon sx={{ fontSize: 14 }} />,
    };
  }
  return {
    background: isDark ? alpha('#4ce1b6', 0.22) : alpha('#4ce1b6', 0.12),
    border: isDark ? alpha('#4ce1b6', 0.48) : alpha('#4ce1b6', 0.32),
    color: '#4ce1b6',
    icon: <CheckCircleIcon sx={{ fontSize: 14 }} />,
  };
};

const BILLING_INTERVALS = [
  { key: 'monthly', label: 'Monthly', multiplier: 1 },
  { key: 'quarterly', label: 'Quarterly', multiplier: 3 },
  { key: 'yearly', label: 'Yearly', multiplier: 12 },
];

const formatCurrencyAmount = (value, currency = 'USD') => {
  const amount = Math.round(Number(value || 0));
  const code = String(currency || 'USD').toUpperCase();
  return `${code} ${amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const formatSubscriptionAmount = (amount, currency) => {
  if (amount == null || amount === '') return '';
  const numericAmount = Math.round(Number(amount || 0));
  const code = String(currency || 'USD').toUpperCase();
  return `${code} ${numericAmount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const SUBSCRIPTION_CATALOG_CACHE = new Map();
const SUBSCRIPTION_CATALOG_STORAGE_PREFIX = 'roomnexa:subscription-catalog:v2:';

const subscriptionCatalogCacheKey = (country, rooms) => (
  `${String(country || 'IN').trim().toUpperCase() || 'IN'}:${Math.max(1, Number(rooms) || 1)}`
);

const readCachedSubscriptionCatalog = (country, rooms) => {
  const key = subscriptionCatalogCacheKey(country, rooms);
  if (SUBSCRIPTION_CATALOG_CACHE.has(key)) return SUBSCRIPTION_CATALOG_CACHE.get(key);
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(`${SUBSCRIPTION_CATALOG_STORAGE_PREFIX}${key}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.packages)) return null;
    SUBSCRIPTION_CATALOG_CACHE.set(key, parsed);
    return parsed;
  } catch {
    return null;
  }
};

const writeCachedSubscriptionCatalog = (country, rooms, payload) => {
  const key = subscriptionCatalogCacheKey(country, rooms);
  SUBSCRIPTION_CATALOG_CACHE.set(key, payload);
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(`${SUBSCRIPTION_CATALOG_STORAGE_PREFIX}${key}`, JSON.stringify(payload));
  } catch {
    /* ignore quota */
  }
};

const normalizeInvoiceDownloadUrl = (url) => {
  const value = String(url || '').trim();
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  return value.startsWith('/') ? value : `/${value}`;
};

const getCatalogIcon = (label = '') => {
  const value = String(label).toLowerCase();
  if (value.includes('whatsapp')) return WhatsAppIcon;
  if (value.includes('ai')) return AutoAwesomeIcon;
  if (value.includes('audit')) return FactCheckIcon;
  if (value.includes('inventory') || value.includes('stock')) return Inventory2Icon;
  if (value.includes('web') || value.includes('channel')) return LanguageIcon;
  if (value.includes('restaurant') || value.includes('pos')) return StorefrontIcon;
  if (value.includes('security') || value.includes('otp')) return SecurityIcon;
  return ExtensionIcon;
};

const normalizeSubscriptionFeatureLabel = (label = '') => {
  const value = String(label || '').trim();
  if (value.toLowerCase() === 'reservation system') return 'Single Room Booking';
  return value;
};

const PACKAGE_SEQUENCE = ['core', 'flex', 'pro', 'ultra'];

const getPackageSequenceIndex = (pkg) => {
  const label = `${pkg?.code || ''} ${pkg?.name || ''} ${pkg?.internal_name || ''}`.toLowerCase();
  const index = PACKAGE_SEQUENCE.findIndex((key) => label.includes(key));
  return index >= 0 ? index : PACKAGE_SEQUENCE.length;
};

const getPackageIncludedModules = (pkg) => {
  const label = `${pkg?.code || ''} ${pkg?.name || ''} ${pkg?.internal_name || ''}`.toLowerCase();
  if (label.includes('core')) {
    return ['PMS', 'Booking Engine', 'Housekeeping', 'Stock Inventory'];
  }
  if (label.includes('flex')) {
    return ['PMS', 'Booking Engine', 'Channel Manager', 'Rates & Inventory', 'Stock Inventory'];
  }
  if (label.includes('pro')) {
    return ['PMS', 'Channel Manager', 'Audit Logs', 'Restaurant / POS', 'Stock Inventory'];
  }
  if (label.includes('ultra')) {
    return ['PMS', 'Channel Manager', 'Audit Logs', 'Restaurant / POS', 'Stock Inventory', 'AI & Advanced Tools'];
  }

  const permissionCodes = Array.isArray(pkg?.permission_codes) ? pkg.permission_codes : [];
  const modules = [];
  const addModule = (name) => {
    if (!modules.includes(name)) modules.push(name);
  };
  permissionCodes.forEach((code) => {
    if (code.includes('booking') || code.includes('housekeeping') || code.includes('configuration')) addModule('PMS');
    if (code.includes('distribution') || code.includes('channel') || code.includes('inventory')) addModule('Channel Manager');
    if (code.includes('restaurant') || code.includes('pos')) addModule('Restaurant/POS');
    if (code.includes('stock')) addModule('Stock Inventory');
    if (code.includes('audit')) addModule('Audit Logs');
    if (code.includes('ai_')) addModule('AI Tools');
  });
  return modules.length ? modules : (pkg?.features || []).slice(0, 5);
};

const LandingSettings = ({
  hotels = [],
  hotelsLoading = false,
  onRefreshHotels = null,
  canManageUsers = false,
  canManageSubscription = false,
  initialTab = null,
}) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === 'dark';
  const [activeTab, setActiveTab] = useState(() => (
    resolveInitialSettingsTab(initialTab, canManageUsers, canManageSubscription)
  ));
  const [billingInterval, setBillingInterval] = useState('yearly');
  const [subscriptionCatalog, setSubscriptionCatalog] = useState({ packages: [], addons: [], currency: 'USD' });
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState('');
  const [catalogNotice, setCatalogNotice] = useState('');
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [scheduleProcessing, setScheduleProcessing] = useState(false);
  const [paymentSuccessDialog, setPaymentSuccessDialog] = useState(null);
  const [selectedPackageCode, setSelectedPackageCode] = useState('');
  const [selectedAddonCodes, setSelectedAddonCodes] = useState([]);
  const [selectedSubscriptionHotelId, setSelectedSubscriptionHotelId] = useState('');
  const [expandedFeatureHotelIds, setExpandedFeatureHotelIds] = useState(() => new Set());
  const [billingDrawerHotel, setBillingDrawerHotel] = useState(null);
  const [couponInput, setCouponInput] = useState('');
  const [couponStatus, setCouponStatus] = useState('idle');
  const [couponMessage, setCouponMessage] = useState('');
  const [couponApplying, setCouponApplying] = useState(false);
  const [couponDetails, setCouponDetails] = useState(() => createEmptyCouponDetails());
  const [checkoutQuote, setCheckoutQuote] = useState(null);
  const [checkoutQuoteLoading, setCheckoutQuoteLoading] = useState(false);
  const [checkoutQuoteError, setCheckoutQuoteError] = useState('');
  const couponAppliedSignatureRef = useRef(null);

  const hotelList = Array.isArray(hotels) ? hotels : [];
  const selectedSubscriptionHotels = useMemo(() => {
    const selectedHotel = hotelList.find((hotel) => String(hotel.id) === String(selectedSubscriptionHotelId));
    return selectedHotel ? [selectedHotel] : (hotelList[0] ? [hotelList[0]] : []);
  }, [hotelList, selectedSubscriptionHotelId]);
  useEffect(() => {
    if (!hotelList.length) {
      setSelectedSubscriptionHotelId('');
      return;
    }
    const selectedStillExists = hotelList.some((hotel) => String(hotel.id) === String(selectedSubscriptionHotelId));
    if (!selectedStillExists) {
      setSelectedSubscriptionHotelId(String(hotelList[0].id));
    }
  }, [hotelList, selectedSubscriptionHotelId]);
  const subscriptionSummary = useMemo(() => {
    const summary = {
      total: hotelList.length,
      active: 0,
      expired: 0,
      upcoming: 0,
      notConfigured: 0,
    };
    hotelList.forEach((hotel) => {
      const status = hotel?.subscription_status || 'not_configured';
      if (status === 'expired') summary.expired += 1;
      else if (status === 'upcoming') summary.upcoming += 1;
      else if (status === 'not_configured') summary.notConfigured += 1;
      else summary.active += 1;
    });
    return summary;
  }, [hotelList]);
  const visiblePackages = useMemo(
    () => (subscriptionCatalog.packages || [])
      .filter((pkg) => {
        const label = `${pkg.code || ''} ${pkg.name || ''} ${pkg.internal_name || ''}`.toLowerCase();
        return !label.includes('free trial') && !label.includes('free-trial');
      })
      .sort((a, b) => (
        getPackageSequenceIndex(a) - getPackageSequenceIndex(b)
        || String(a.name || '').localeCompare(String(b.name || ''))
      )),
    [subscriptionCatalog.packages]
  );
  const selectedPackage = useMemo(
    () => visiblePackages.find((pkg) => pkg.code === selectedPackageCode) || null,
    [selectedPackageCode, visiblePackages]
  );
  const selectedSubscriptionRoomCount = useMemo(() => {
    const capturedRooms = selectedSubscriptionHotels.reduce(
      (sum, hotel) => sum + Number(hotel.subscription_room_count || 0),
      0
    );
    if (capturedRooms > 0) return capturedRooms;
    const configuredRooms = selectedSubscriptionHotels.reduce(
      (sum, hotel) => sum + Number(hotel.room_count || hotel.configured_room_count || 0),
      0
    );
    return configuredRooms > 0 ? configuredRooms : 1;
  }, [selectedSubscriptionHotels]);
  const selectedCatalogHotel = selectedSubscriptionHotels[0] || hotelList[0] || {};
  const subscriptionCatalogCountry = selectedCatalogHotel.country || 'IN';
  const resolveCatalogItemCurrency = (item) => (
    String(item?.currency || subscriptionCatalog.currency || 'USD').trim().toUpperCase() || 'USD'
  );
  const selectedSubscriptionIsActive = selectedSubscriptionHotels.some((hotel) => hotel.subscription_status === 'active');
  const selectedSubscriptionExpiresAt = selectedSubscriptionHotels
    .map((hotel) => hotel.subscription_expires_at)
    .filter(Boolean)
    .sort()[0];
  const selectedSubscriptionDaysLeft = useMemo(() => {
    if (!selectedSubscriptionExpiresAt) return null;
    const expiryTime = new Date(selectedSubscriptionExpiresAt).getTime();
    if (Number.isNaN(expiryTime)) return null;
    return Math.max(0, Math.ceil((expiryTime - Date.now()) / (1000 * 60 * 60 * 24)));
  }, [selectedSubscriptionExpiresAt]);
  const isSubscriptionRenewalWindow = useMemo(() => {
    if (!selectedSubscriptionIsActive || !selectedSubscriptionExpiresAt) return false;
    const expiryTime = new Date(selectedSubscriptionExpiresAt).getTime();
    if (Number.isNaN(expiryTime)) return false;
    const daysUntilExpiry = (expiryTime - Date.now()) / (1000 * 60 * 60 * 24);
    return daysUntilExpiry <= 31;
  }, [selectedSubscriptionExpiresAt, selectedSubscriptionIsActive]);
  const isPackagePaymentLocked = selectedSubscriptionIsActive && !isSubscriptionRenewalWindow;
  const activePackageNames = useMemo(() => (
    Array.from(new Set(selectedSubscriptionHotels.flatMap((hotel) => hotel.subscription_package_names || [])))
  ), [selectedSubscriptionHotels]);
  const activePackageCodes = useMemo(() => (
    Array.from(new Set(selectedSubscriptionHotels.flatMap((hotel) => (
      (hotel.subscription_package_details || []).map((detail) => detail.code).filter(Boolean)
    ))))
  ), [selectedSubscriptionHotels]);
  const currentActivePackage = useMemo(() => (
    visiblePackages.find((pkg) => (
      activePackageCodes.includes(pkg.code)
      || activePackageNames.includes(pkg.internal_name)
      || activePackageNames.includes(pkg.name)
      || activePackageNames.includes(pkg.code)
    )) || null
  ), [activePackageCodes, activePackageNames, visiblePackages]);
  const includedSubscriptionPermissionCodes = useMemo(() => {
    const codes = new Set(currentActivePackage?.permission_codes || []);
    selectedSubscriptionHotels.forEach((hotel) => {
      (hotel.subscription_addon_permission_codes || []).forEach((code) => {
        if (code) codes.add(code);
      });
    });
    return codes;
  }, [currentActivePackage, selectedSubscriptionHotels]);
  const availableAddons = useMemo(() => (
    (subscriptionCatalog.addons || []).filter((addon) => {
      const permissionCodes = Array.isArray(addon.permission_codes) ? addon.permission_codes : [];
      if (!permissionCodes.length) return true;
      return !permissionCodes.every((code) => includedSubscriptionPermissionCodes.has(code));
    })
  ), [includedSubscriptionPermissionCodes, subscriptionCatalog.addons]);
  const selectedAddons = useMemo(
    () => availableAddons.filter((addon) => selectedAddonCodes.includes(addon.code)),
    [availableAddons, selectedAddonCodes]
  );
  const monthlyEstimate = useMemo(() => {
    const packageAmount = isPackagePaymentLocked ? 0 : Number(selectedPackage?.charged_pricing?.[billingInterval] || 0);
    const addonsAmount = selectedAddons.reduce(
      (sum, addon) => sum + Number(addon?.charged_pricing?.[billingInterval] || 0),
      0
    );
    return packageAmount + addonsAmount;
  }, [billingInterval, isPackagePaymentLocked, selectedAddons, selectedPackage]);
  const paymentPackage = useMemo(
    () => (isPackagePaymentLocked ? (currentActivePackage || selectedPackage) : selectedPackage),
    [currentActivePackage, isPackagePaymentLocked, selectedPackage]
  );
  const couponApplied = couponStatus === 'applied';
  const couponContextSignature = useMemo(() => (
    buildCouponContextSignature({
      packageId: paymentPackage?.code,
      billingInterval,
      rooms: selectedSubscriptionRoomCount,
      region: subscriptionCatalogCountry,
      addonCodes: selectedAddonCodes,
      subscriptionAddonOnly: isPackagePaymentLocked,
    })
  ), [
    billingInterval,
    isPackagePaymentLocked,
    paymentPackage?.code,
    selectedAddonCodes,
    selectedSubscriptionRoomCount,
    subscriptionCatalogCountry,
  ]);
  const checkoutDisplayTotal = useMemo(() => (
    resolveCheckoutDisplayTotal({
      catalogEstimateMajor: monthlyEstimate,
      checkoutQuote,
      couponApplied,
      couponDetails,
    })
  ), [
    checkoutQuote,
    couponApplied,
    couponDetails,
    monthlyEstimate,
  ]);
  const checkoutSavingsMajor = useMemo(() => (
    resolveCheckoutSavingsMajor({
      catalogEstimateMajor: monthlyEstimate,
      checkoutQuote,
      couponApplied,
      couponDetails,
    })
  ), [
    checkoutQuote,
    couponApplied,
    couponDetails,
    monthlyEstimate,
  ]);
  useEffect(() => {
    setSelectedAddonCodes((prev) => prev.filter((code) => availableAddons.some((addon) => addon.code === code)));
  }, [availableAddons]);
  useEffect(() => {
    if (!couponApplied) return;
    if (!couponAppliedSignatureRef.current) return;
    if (couponAppliedSignatureRef.current === couponContextSignature) return;
    couponAppliedSignatureRef.current = null;
    setCouponStatus('idle');
    setCouponMessage('Coupon cleared because plan, rooms, billing, or add-ons changed.');
    setCouponDetails(createEmptyCouponDetails());
  }, [couponApplied, couponContextSignature]);
  const activePlanLabels = useMemo(() => (
    Array.from(new Set(selectedSubscriptionHotels.flatMap((hotel) => (
      (hotel.subscription_package_details || []).map((detail) => (
        [
          `${detail.name} (${detail.billing_interval || hotel.subscription_billing_interval || 'yearly'})`,
          Number(detail.room_count || 0) > 0 ? `${detail.room_count} rooms` : '',
          formatSubscriptionAmount(
            detail.subscription_amount,
            Number(detail.subscription_amount || 0) > 0 ? detail.subscription_currency : hotel.currency
          ),
        ].filter(Boolean).join(' - ')
      ))
    ))))
  ), [selectedSubscriptionHotels]);
  const currentSubscriptionTotal = useMemo(() => {
    const details = selectedSubscriptionHotels.flatMap((hotel) => hotel.subscription_package_details || []);
    return details.reduce((sum, detail) => sum + Number(detail.subscription_amount || 0), 0);
  }, [selectedSubscriptionHotels]);
  const currentSubscriptionCurrency = useMemo(() => {
    const detail = selectedSubscriptionHotels
      .flatMap((hotel) => hotel.subscription_package_details || [])
      .find((item) => Number(item.subscription_amount || 0) > 0 && item.subscription_currency);
    if (detail?.subscription_currency) return detail.subscription_currency;
    const hotel = selectedSubscriptionHotels.find((item) => item.currency) || hotelList.find((item) => item.currency);
    return hotel?.currency || 'USD';
  }, [hotelList, selectedSubscriptionHotels]);
  const displayCurrency = resolveCatalogItemCurrency(selectedPackage || selectedAddons[0]);

  useEffect(() => {
    if (!paymentPackage?.code) {
      setCheckoutQuote(null);
      setCheckoutQuoteError('');
      return undefined;
    }
    let cancelled = false;
    setCheckoutQuoteLoading(true);
    setCheckoutQuoteError('');
    const { customerEmail, customerPhone } = getSubscriptionCheckoutContact(selectedCatalogHotel);
    const timer = window.setTimeout(() => {
      fetchWebsiteSubscriptionQuote({
        packageId: paymentPackage.code,
        billingInterval,
        rooms: selectedSubscriptionRoomCount,
        region: subscriptionCatalogCountry,
        paymentCurrency: displayCurrency,
        addonCodes: selectedAddonCodes,
        couponCode: couponApplied ? couponDetails.code : '',
        customerEmail: couponApplied ? customerEmail : '',
        customerPhone: couponApplied ? customerPhone : '',
        subscriptionAddonOnly: isPackagePaymentLocked,
      })
        .then((quote) => {
          if (!cancelled) setCheckoutQuote(quote);
        })
        .catch((error) => {
          if (cancelled) return;
          setCheckoutQuote(null);
          const message = String(error?.message || '');
          if (couponApplied && /email|phone|contact/i.test(message)) return;
          setCheckoutQuoteError(message || 'Unable to load checkout quote.');
        })
        .finally(() => {
          if (!cancelled) setCheckoutQuoteLoading(false);
        });
    }, 180);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    billingInterval,
    couponApplied,
    couponDetails.code,
    displayCurrency,
    isPackagePaymentLocked,
    paymentPackage?.code,
    selectedAddonCodes,
    selectedCatalogHotel,
    selectedSubscriptionRoomCount,
    subscriptionCatalogCountry,
  ]);

  const handleTabChange = (_, nextTab) => {
    if (nextTab === 'users' && !canManageUsers) return;
    if (nextTab === 'subscription' && !canManageSubscription) return;
    setActiveTab(nextTab);
    const params = new URLSearchParams(location.search);
    params.set('view', 'settings');
    params.set('tab', nextTab);
    navigate(`/onboarding/landing/?${params.toString()}`, { replace: false });
  };

  useEffect(() => {
    setActiveTab((currentTab) => {
      const nextTab = resolveInitialSettingsTab(initialTab, canManageUsers, canManageSubscription);
      if (initialTab === 'subscription' && canManageSubscription) return 'subscription';
      if (initialTab === 'users' && canManageUsers) return 'users';
      if (!canManageSubscription && currentTab === 'subscription') return nextTab;
      if (!canManageUsers && currentTab === 'users') return nextTab;
      return currentTab || nextTab;
    });
  }, [initialTab, canManageUsers, canManageSubscription]);

  useEffect(() => {
    if (!canManageSubscription || activeTab !== 'subscription') return;
    if (hotelsLoading && !hotelList.length) return;
    let cancelled = false;
    const cached = readCachedSubscriptionCatalog(subscriptionCatalogCountry, selectedSubscriptionRoomCount);
    if (cached) {
      setSubscriptionCatalog({
        packages: cached.packages || [],
        addons: cached.addons || [],
        currency: cached.currency || 'USD',
        payment_gateway: cached.payment_gateway || null,
      });
      setCatalogLoading(false);
    } else {
      setCatalogLoading(true);
    }
    setCatalogError('');
    const fetchCatalog = async () => {
      try {
        const response = await axios.get('/onboarding/api/subscription/catalog/', {
          params: {
            country: subscriptionCatalogCountry,
            rooms: selectedSubscriptionRoomCount,
          },
          withCredentials: true,
        });
        if (cancelled) return;
        const payload = response.data || {};
        const nextCatalog = {
          packages: payload.packages || [],
          addons: payload.addons || [],
          currency: payload.currency || 'USD',
          payment_gateway: payload.payment_gateway || null,
        };
        writeCachedSubscriptionCatalog(subscriptionCatalogCountry, selectedSubscriptionRoomCount, nextCatalog);
        setSubscriptionCatalog(nextCatalog);
      } catch (err) {
        if (!cancelled && !cached) {
          setCatalogError(err.response?.data?.error || 'Failed to load subscription packages.');
        }
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    };
    fetchCatalog();
    return () => {
      cancelled = true;
    };
  }, [
    activeTab,
    canManageSubscription,
    hotelList.length,
    hotelsLoading,
    selectedSubscriptionRoomCount,
    subscriptionCatalogCountry,
  ]);

  useEffect(() => {
    const packages = visiblePackages || [];
    if (!packages.length) return;
    const currentPackage = packages.find((pkg) => (
      activePackageCodes.includes(pkg.code)
      || activePackageNames.includes(pkg.internal_name)
      || activePackageNames.includes(pkg.name)
      || activePackageNames.includes(pkg.code)
    ));
    setSelectedPackageCode((current) => {
      if (currentPackage?.code) return currentPackage.code;
      if (current && packages.some((pkg) => pkg.code === current)) return current;
      return '';
    });
  }, [activePackageCodes, activePackageNames, visiblePackages]);

  const handleDownloadInvoice = (invoiceUrl) => {
    const url = normalizeInvoiceDownloadUrl(invoiceUrl);
    if (!url) {
      setCatalogError('Invoice is not available yet for this payment.');
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleSchedulePackageChange = async () => {
    if (!selectedPackage || !selectedCatalogHotel?.id || scheduleProcessing) return;
    if (currentActivePackage?.code === selectedPackage.code) {
      setCatalogNotice('');
      setCatalogError('Select a different package to schedule a change.');
      return;
    }

    setScheduleProcessing(true);
    setCatalogError('');
    setCatalogNotice('');
    try {
      const { data } = await axios.post('/onboarding/api/subscription/schedule-change/', {
        hotelId: selectedCatalogHotel.id,
        packageCode: selectedPackage.code,
        billingInterval: billingInterval,
        roomCount: selectedSubscriptionRoomCount,
      }, { withCredentials: true });
      setCatalogNotice(data?.message || `${selectedPackage.name} is scheduled after the current subscription ends.`);
    } catch (err) {
      setCatalogError(err.response?.data?.error || 'Failed to schedule package change.');
    } finally {
      setScheduleProcessing(false);
    }
  };

  const handleApplyCoupon = useCallback(async () => {
    const code = normalizeCouponCode(couponInput);
    if (!code) {
      setCouponStatus('error');
      setCouponMessage('Enter a coupon code to apply.');
      setCouponDetails(createEmptyCouponDetails());
      return;
    }
    if (!paymentPackage?.code) {
      setCouponStatus('error');
      setCouponMessage('Select a package before applying a coupon.');
      return;
    }
    const { customerEmail, customerPhone } = getSubscriptionCheckoutContact(selectedCatalogHotel);
    if (!customerEmail || !customerPhone) {
      setCouponStatus('error');
      setCouponMessage('Customer email and hotel contact phone are required before applying a coupon.');
      setCouponDetails(createEmptyCouponDetails());
      couponAppliedSignatureRef.current = null;
      return;
    }

    setCouponApplying(true);
    setCouponStatus('idle');
    setCouponMessage('');
    try {
      const result = await validateWebsiteSubscriptionCoupon({
        packageId: paymentPackage.code,
        billingInterval,
        rooms: selectedSubscriptionRoomCount,
        region: subscriptionCatalogCountry,
        addonCodes: selectedAddonCodes,
        couponCode: code,
        customerEmail,
        customerPhone,
        subscriptionAddonOnly: isPackagePaymentLocked,
      });
      setCouponInput(result.details.code || code);
      setCouponStatus('applied');
      setCouponMessage(result.message || `Coupon applied${result.details.discountPercent ? ` (${result.details.discountPercent}% off)` : ''}.`);
      setCouponDetails(result.details);
      couponAppliedSignatureRef.current = couponContextSignature;
    } catch (error) {
      setCouponStatus('error');
      setCouponMessage(error?.message || 'Unable to validate coupon right now.');
      setCouponDetails(createEmptyCouponDetails());
      couponAppliedSignatureRef.current = null;
    } finally {
      setCouponApplying(false);
    }
  }, [
    billingInterval,
    couponContextSignature,
    couponInput,
    isPackagePaymentLocked,
    paymentPackage?.code,
    selectedAddonCodes,
    selectedCatalogHotel,
    selectedSubscriptionRoomCount,
    subscriptionCatalogCountry,
  ]);

  const handleOpenPaymentGateway = async () => {
    if (!selectedPackage || paymentProcessing) return;
    if (!paymentPackage?.code) return;
    const { customerName, customerEmail, customerPhone, djangoContext } = getSubscriptionCheckoutContact(selectedCatalogHotel);
    const organisation = djangoContext.userOrganisation || {};
    const hotel = selectedCatalogHotel || {};

    if (!customerName || !customerEmail || !customerPhone) {
      setCatalogNotice('');
      setCatalogError('Customer name, email, and hotel contact phone are required to open Razorpay checkout.');
      return;
    }

    const billingForPayment = toApiBillingInterval(billingInterval);
    const validityDays = { monthly: 30, quarterly: 90, yearly: 365 }[billingInterval] || 30;
    const addonsPayload = buildAddonsPayload(selectedAddonCodes);
    const couponFields = buildOrderCouponFields({
      couponApplied,
      couponDetails,
    });
    const selectedPlanChargedAmount = Math.max(
      1,
      Math.round(Number(
        isPackagePaymentLocked
          ? monthlyEstimate
          : (selectedPackage?.charged_pricing?.[billingInterval] || monthlyEstimate || 0)
      ))
    );
    const displayTotalMajor = Math.max(1, Math.round(Number(checkoutDisplayTotal || monthlyEstimate || 0)));

    setPaymentProcessing(true);
    setCatalogError('');
    setCatalogNotice('');
    try {
      const sdkLoaded = await loadRazorpayScript();
      if (!sdkLoaded || !window.Razorpay) {
        throw new Error('Unable to load Razorpay checkout. Please try again.');
      }

      const { data: order } = await axios.post('/api/v1/payments/orders/', {
        packageId: paymentPackage.code,
        hotelId: hotel.id,
        billing: billingForPayment,
        rooms: selectedSubscriptionRoomCount,
        region: subscriptionCatalogCountry,
        paymentCurrency: displayCurrency,
        subscriptionAddonOnly: isPackagePaymentLocked,
        displayTotalMajor,
        addons: addonsPayload,
        ...couponFields,
        customerName,
        customerEmail,
        customerPhone,
        organizationName: organisation.name || '',
        propertyName: hotel.name || djangoContext.selectedHotel?.name || 'Hotel subscription',
        propertyLocation: [hotel.city, hotel.country].filter(Boolean).join(', '),
        propertyCity: hotel.city || '',
        propertyCountry: hotel.country || subscriptionCatalogCountry,
        selectedPlanName: paymentPackage.name,
        selectedPlanTagline: paymentPackage.tagline || paymentPackage.description || '',
        selectedPlanTrial: (() => {
          const trialDays = Number(
            paymentPackage.trial_days_by_billing?.[billingInterval] ?? paymentPackage.trial_days
          );
          return trialDays > 0 ? `${trialDays}-day trial` : '';
        })(),
        selectedPlanMonthlyPriceInr: Math.max(0, Math.round(Number(paymentPackage?.pricing?.[billingInterval] || 0))),
        selectedPlanChargedAmountInr: selectedPlanChargedAmount,
        selectedPlanBaseAmountInr: selectedPlanChargedAmount,
        addonsAmountInr: Math.max(0, Math.round(monthlyEstimate - selectedPlanChargedAmount)),
        selectedPlanBillingLabel: billingInterval,
        selectedPlanBillingSuffix: `${selectedSubscriptionRoomCount} rooms`,
        validityDays,
      }, { withCredentials: true });

      const checkout = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: order.name || 'RoomNexa',
        description: order.description || `${selectedPackage.name} plan checkout`,
        order_id: order.order_id,
        prefill: {
          name: customerName,
          email: customerEmail,
          contact: customerPhone,
        },
        theme: {
          color: '#4ce1b6',
        },
        handler: async (response) => {
          try {
            const { data: verifyResult } = await axios.post('/api/v1/payments/verify/', {
              razorpay_order_id: response?.razorpay_order_id || order.order_id,
              razorpay_payment_id: response?.razorpay_payment_id || '',
              razorpay_signature: response?.razorpay_signature || '',
            }, { withCredentials: true });
            const invoiceDownloadUrl = normalizeInvoiceDownloadUrl(verifyResult?.invoiceDownloadUrl);
            setCatalogError('');
            setCatalogNotice('Payment completed successfully. Subscription payment verification is recorded.');
            setPaymentSuccessDialog({
              packageName: paymentPackage.name,
              amount: order.amount,
              currency: order.currency,
              orderId: response?.razorpay_order_id || order.order_id,
              paymentId: response?.razorpay_payment_id || '',
              invoiceDownloadUrl,
            });
          } catch (verifyError) {
            setCatalogNotice('');
            setCatalogError(verifyError.response?.data?.detail || 'Payment completed, but verification failed. Please contact support.');
          }
        },
        modal: {
          ondismiss: () => {
            setPaymentProcessing(false);
          },
        },
      });

      checkout.on('payment.failed', (response) => {
        setCatalogNotice('');
        setCatalogError(response?.error?.description || 'Payment failed. Please try again.');
      });
      checkout.open();
    } catch (err) {
      setCatalogNotice('');
      setCatalogError(err.response?.data?.detail || err.message || 'Unable to open payment gateway.');
    } finally {
      setPaymentProcessing(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <DashboardHeader
          title="Settings"
          forceDark={isMobileShellLayout()}
          breadcrumbs={[
            { label: 'Home', path: '/onboarding/landing/' },
            { label: 'Settings' },
          ]}
          navLinks={[
            { url: '/onboarding/landing/?view=dashboard', label: 'Dashboard' },
          ]}
        />
      </Box>

      <Card
        sx={{
          borderRadius: '18px',
          border: `1px solid ${isDark ? alpha('#ffffff', 0.12) : alpha('#0f172a', 0.1)}`,
          background: isDark
            ? 'linear-gradient(155deg, rgba(33,33,39,0.96), rgba(24,24,29,0.98))'
            : 'linear-gradient(155deg, rgba(255,255,255,0.98), rgba(245,250,255,0.98))',
          backdropFilter: 'blur(10px)',
          boxShadow: isDark
            ? '0 12px 34px rgba(2, 6, 23, 0.45)'
            : '0 12px 34px rgba(15, 23, 42, 0.08)',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: 2, pb: 1.5 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="settings tabs"
            sx={{
              minHeight: 52,
              '& .MuiTabs-indicator': {
                height: 0,
              },
            }}
          >
            <Tab
              icon={<ManageAccountsIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Users"
              value="users"
              disabled={!canManageUsers}
              {...tabA11yProps('users')}
              sx={{
                minHeight: 44,
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: '12px',
                mr: 1,
                px: 2.2,
                color: isDark ? '#9aa4b2' : '#64748b',
                '&.Mui-selected': {
                  color: '#4ce1b6',
                  background: isDark
                    ? 'linear-gradient(135deg, rgba(76,225,182,0.20), rgba(76,225,182,0.08))'
                    : 'linear-gradient(135deg, rgba(76,225,182,0.16), rgba(76,225,182,0.08))',
                },
              }}
            />
            {canManageSubscription && (
              <Tab
                icon={<CreditCardIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label="Subscription"
                value="subscription"
                {...tabA11yProps('subscription')}
                sx={{
                  minHeight: 44,
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: '12px',
                  px: 2.2,
                  color: isDark ? '#9aa4b2' : '#64748b',
                  '&.Mui-selected': {
                    color: '#4ce1b6',
                    background: isDark
                      ? 'linear-gradient(135deg, rgba(76,225,182,0.20), rgba(76,225,182,0.08))'
                      : 'linear-gradient(135deg, rgba(76,225,182,0.16), rgba(76,225,182,0.08))',
                  },
                }}
              />
            )}
          </Tabs>
        </Box>

        <Divider sx={{ borderColor: isDark ? alpha('#ffffff', 0.12) : alpha('#0f172a', 0.08) }} />

        <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Box
            role="tabpanel"
            hidden={activeTab !== 'users'}
            id="settings-panel-users"
            aria-labelledby="settings-tab-users"
          >
            {activeTab === 'users' && (
              canManageUsers ? (
                <LandingUsers embedded />
              ) : (
                <Box
                  sx={{
                    borderRadius: '12px',
                    p: 2.5,
                    border: `1px solid ${isDark ? alpha('#ffffff', 0.1) : alpha('#0f172a', 0.1)}`,
                    backgroundColor: isDark ? alpha('#0f172a', 0.35) : '#f8fafc',
                  }}
                >
                  <Typography sx={{ color: isDark ? '#ddd' : '#1e293b', fontWeight: 600 }}>
                    You do not have permission to manage users.
                  </Typography>
                </Box>
              )
            )}
          </Box>

          {canManageSubscription && (
            <Box
              role="tabpanel"
              hidden={activeTab !== 'subscription'}
              id="settings-panel-subscription"
              aria-labelledby="settings-tab-subscription"
            >
              {activeTab === 'subscription' && (
              <Box>
                <Box
                  sx={{
                    mb: 2.2,
                    display: 'grid',
                    gap: 1.4,
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, minmax(0, 1fr))',
                      md: 'repeat(5, minmax(0, 1fr))',
                    },
                  }}
                >
                  {[
                    {
                      label: 'Hotels',
                      value: subscriptionSummary.total,
                      color: isDark ? '#e2e8f0' : '#0f172a',
                      borderColor: isDark ? '#334155' : '#dbe6f0',
                      glow: '#38bdf8',
                      icon: <StorefrontIcon sx={{ fontSize: 19 }} />,
                    },
                    {
                      label: 'Active',
                      value: subscriptionSummary.active,
                      color: '#4ce1b6',
                      borderColor: isDark ? alpha('#4ce1b6', 0.38) : alpha('#4ce1b6', 0.28),
                      glow: '#4ce1b6',
                      icon: <CheckCircleIcon sx={{ fontSize: 19 }} />,
                    },
                    {
                      label: 'Expired',
                      value: subscriptionSummary.expired,
                      color: '#ff4861',
                      borderColor: isDark ? alpha('#ff4861', 0.38) : alpha('#ff4861', 0.28),
                      glow: '#ff4861',
                      icon: <ErrorOutlineIcon sx={{ fontSize: 19 }} />,
                    },
                    {
                      label: 'Upcoming',
                      value: subscriptionSummary.upcoming,
                      color: '#f6da6e',
                      borderColor: isDark ? alpha('#f6da6e', 0.38) : alpha('#f6da6e', 0.28),
                      glow: '#f6da6e',
                      icon: <AccessTimeIcon sx={{ fontSize: 19 }} />,
                    },
                    {
                      label: 'Not Configured',
                      value: subscriptionSummary.notConfigured,
                      color: isDark ? '#cbd5e1' : '#475569',
                      borderColor: isDark ? alpha('#94a3b8', 0.4) : alpha('#64748b', 0.24),
                      glow: '#94a3b8',
                      icon: <SecurityIcon sx={{ fontSize: 19 }} />,
                    },
                  ].map((metric) => (
                    <Box
                      key={metric.label}
                      sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        borderRadius: '16px',
                        border: `1px solid ${metric.borderColor}`,
                        background: isDark
                          ? `linear-gradient(145deg, ${alpha(metric.glow, 0.13)}, ${alpha('#020617', 0.7)})`
                          : `linear-gradient(145deg, ${alpha(metric.glow, 0.1)}, #ffffff)`,
                        p: 1.6,
                        height: '100%',
                        boxShadow: isDark ? `0 16px 36px ${alpha(metric.glow, 0.08)}` : `0 16px 30px ${alpha('#0f172a', 0.06)}`,
                        '&:before': {
                          content: '""',
                          position: 'absolute',
                          inset: 'auto -20% -55% auto',
                          width: 110,
                          height: 110,
                          borderRadius: '50%',
                          background: alpha(metric.glow, 0.16),
                        },
                      }}
                    >
                      <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                        <Box>
                          <Typography sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.7, fontWeight: 800 }}>
                            {metric.label}
                          </Typography>
                          <Typography sx={{ color: metric.color, fontWeight: 900, fontSize: 26, lineHeight: 1.1 }}>
                            {metric.value}
                          </Typography>
                        </Box>
                        <Box sx={{ width: 34, height: 34, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: metric.color, bgcolor: alpha(metric.glow, isDark ? 0.16 : 0.12) }}>
                          {metric.icon}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>

                <Box
                  sx={{
                    mb: 2.2,
                    p: 1.4,
                    borderRadius: '16px',
                    border: `1px solid ${isDark ? alpha('#ffffff', 0.12) : alpha('#0f172a', 0.08)}`,
                    backgroundColor: isDark ? alpha('#020617', 0.24) : '#ffffff',
                  }}
                >
                  <Typography sx={{ color: isDark ? '#e2e8f0' : '#0f172a', fontWeight: 900, mb: 1 }}>
                    Manage subscription for
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    {hotelList.map((hotel) => {
                      const selected = String(selectedSubscriptionHotelId) === String(hotel.id);
                      return (
                        <Button
                          key={hotel.id}
                          size="small"
                          variant={selected ? 'contained' : 'outlined'}
                          onClick={() => setSelectedSubscriptionHotelId(hotel.id)}
                          sx={{
                            textTransform: 'none',
                            borderRadius: 999,
                            fontWeight: 800,
                            bgcolor: selected ? '#4ce1b6' : 'transparent',
                            color: selected ? '#06221b' : (isDark ? '#d1fae5' : '#0f766e'),
                            borderColor: alpha('#4ce1b6', 0.45),
                            '&:hover': {
                              bgcolor: selected ? '#43d6ad' : alpha('#4ce1b6', 0.1),
                              borderColor: '#4ce1b6',
                            },
                          }}
                        >
                          {hotel.name || `Hotel #${hotel.id}`}
                        </Button>
                      );
                    })}
                  </Box>
                </Box>

                <Box
                  sx={{
                    mb: 2.2,
                    borderRadius: '18px',
                    border: `1px solid ${isDark ? alpha('#4ce1b6', 0.24) : alpha('#0f766e', 0.14)}`,
                    background: isDark
                      ? 'linear-gradient(135deg, rgba(76,225,182,0.12), rgba(14,165,233,0.06))'
                      : 'linear-gradient(135deg, rgba(236,253,245,0.9), rgba(240,249,255,0.9))',
                    p: { xs: 1.5, sm: 2 },
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography sx={{ color: isDark ? '#ecfeff' : '#0f172a', fontWeight: 900, fontSize: { xs: 18, md: 22 } }}>
                          Current plan
                        </Typography>
                        {selectedSubscriptionDaysLeft != null ? (
                          <Chip
                            icon={<AccessTimeIcon />}
                            label={`${selectedSubscriptionDaysLeft} day${selectedSubscriptionDaysLeft === 1 ? '' : 's'} left`}
                            size="small"
                            sx={{
                              height: 30,
                              borderRadius: 999,
                              fontWeight: 950,
                              letterSpacing: 0.1,
                              color: selectedSubscriptionDaysLeft <= 31 ? '#7c2d12' : '#064e3b',
                              border: `1px solid ${selectedSubscriptionDaysLeft <= 31 ? alpha('#fb923c', 0.45) : alpha('#4ce1b6', 0.45)}`,
                              background: selectedSubscriptionDaysLeft <= 31
                                ? 'linear-gradient(135deg, rgba(254,215,170,0.96), rgba(253,186,116,0.72))'
                                : 'linear-gradient(135deg, rgba(209,250,229,0.96), rgba(76,225,182,0.42))',
                              boxShadow: selectedSubscriptionDaysLeft <= 31
                                ? `0 10px 24px ${alpha('#fb923c', 0.22)}`
                                : `0 10px 24px ${alpha('#4ce1b6', 0.18)}`,
                              '& .MuiChip-icon': {
                                color: 'inherit',
                                fontSize: 17,
                              },
                            }}
                          />
                        ) : null}
                      </Box>
                      <Typography sx={{ color: isDark ? '#a7f3d0' : '#0f766e', fontSize: 13 }}>
                        Current: {activePlanLabels.length ? activePlanLabels.join(', ') : (activePackageNames.length ? activePackageNames.join(', ') : 'No package assigned')}
                      </Typography>
                      <Typography sx={{ color: isDark ? '#ecfeff' : '#0f172a', fontWeight: 900, fontSize: 14, mt: 0.4 }}>
                        Total price: {selectedPackage
                          ? `${formatCurrencyAmount(checkoutDisplayTotal, displayCurrency)} billed ${billingInterval}`
                          : formatSubscriptionAmount(currentSubscriptionTotal, currentSubscriptionCurrency)}
                        {selectedPackage && couponApplied && checkoutSavingsMajor > 0 ? (
                          <Typography component="span" sx={{ color: '#4ce1b6', fontWeight: 800, fontSize: 12, ml: 1 }}>
                            (save {formatCurrencyAmount(checkoutSavingsMajor, displayCurrency)})
                          </Typography>
                        ) : null}
                      </Typography>
                      {isPackagePaymentLocked ? (
                        <Typography sx={{ color: isDark ? '#a7f3d0' : '#0f766e', fontSize: 12, fontWeight: 800, mt: 0.4 }}>
                          Current package is active. You can buy add-ons now; package renewal, upgrade, or downgrade opens in the final month before expiry.
                        </Typography>
                      ) : null}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                      {BILLING_INTERVALS.map((interval) => (
                        <Button
                          key={interval.key}
                          size="small"
                          variant={billingInterval === interval.key ? 'contained' : 'outlined'}
                          onClick={() => setBillingInterval(interval.key)}
                          sx={{
                            textTransform: 'none',
                            borderRadius: 999,
                            fontWeight: 800,
                            width: { xs: 88, sm: 94 },
                            height: 44,
                            bgcolor: billingInterval === interval.key ? '#4ce1b6' : 'transparent',
                            color: billingInterval === interval.key ? '#06221b' : (isDark ? '#d1fae5' : '#0f766e'),
                            borderColor: alpha('#4ce1b6', 0.45),
                            '&:hover': {
                              bgcolor: billingInterval === interval.key ? '#43d6ad' : alpha('#4ce1b6', 0.1),
                              borderColor: '#4ce1b6',
                            },
                          }}
                        >
                          {interval.label}
                        </Button>
                      ))}
                    </Box>
                  </Box>

                  {catalogError ? (
                    <Typography sx={{ color: '#ff4861', fontWeight: 700, mb: 1.5 }}>{catalogError}</Typography>
                  ) : null}
                  {catalogNotice ? (
                    <Typography sx={{ color: '#4ce1b6', fontWeight: 800, mb: 1.5 }}>{catalogNotice}</Typography>
                  ) : null}

                  {catalogLoading ? (
                    <>
                      <Grid container spacing={1.5}>
                        {Array.from({ length: 4 }).map((_, index) => (
                          <Grid item xs={12} sm={6} md={3} key={`package-skeleton-${index}`} sx={{ display: 'flex' }}>
                            <Box
                              sx={{
                                width: '100%',
                                minHeight: 330,
                                borderRadius: '18px',
                                p: 2.2,
                                border: `1px solid ${isDark ? alpha('#fff', 0.1) : alpha('#0f172a', 0.08)}`,
                                background: isDark
                                  ? 'linear-gradient(180deg, rgba(15,23,42,0.9), rgba(2,6,23,0.88))'
                                  : '#ffffff',
                              }}
                            >
                              <Skeleton variant="text" width="46%" height={26} sx={{ bgcolor: isDark ? alpha('#fff', 0.12) : undefined }} />
                              <Skeleton variant="text" width="82%" height={18} sx={{ bgcolor: isDark ? alpha('#fff', 0.1) : undefined }} />
                              <Skeleton variant="text" width="70%" height={18} sx={{ bgcolor: isDark ? alpha('#fff', 0.1) : undefined }} />
                              <Skeleton variant="text" width="72%" height={48} sx={{ mt: 2, bgcolor: isDark ? alpha('#4ce1b6', 0.14) : undefined }} />
                              <Skeleton variant="text" width="88%" height={18} sx={{ bgcolor: isDark ? alpha('#fff', 0.1) : undefined }} />
                              <Skeleton variant="text" width="54%" height={18} sx={{ mb: 1.5, bgcolor: isDark ? alpha('#fff', 0.1) : undefined }} />
                              {Array.from({ length: 5 }).map((__, rowIndex) => (
                                <Skeleton
                                  key={`package-skeleton-line-${rowIndex}`}
                                  variant="rounded"
                                  width={`${72 + (rowIndex % 2) * 14}%`}
                                  height={18}
                                  sx={{ mb: 0.7, borderRadius: 1, bgcolor: isDark ? alpha('#fff', 0.08) : undefined }}
                                />
                              ))}
                              <Skeleton variant="rounded" width="100%" height={36} sx={{ mt: 2, borderRadius: '10px', bgcolor: alpha('#4ce1b6', 0.28) }} />
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                      <Box sx={{ mt: 2 }}>
                        <Skeleton variant="text" width={150} height={24} sx={{ mb: 1, bgcolor: isDark ? alpha('#fff', 0.12) : undefined }} />
                        <Grid container spacing={1}>
                          {Array.from({ length: 4 }).map((_, index) => (
                            <Grid item xs={12} sm={6} md={3} key={`addon-skeleton-${index}`} sx={{ display: 'flex' }}>
                              <Skeleton
                                variant="rounded"
                                width="100%"
                                height={98}
                                sx={{
                                  borderRadius: '14px',
                                  bgcolor: isDark ? alpha('#fff', 0.08) : undefined,
                                }}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Grid container spacing={1.5}>
                        {visiblePackages.map((pkg) => {
                          const selected = selectedPackageCode === pkg.code;
                          const isCurrentActivePackage = currentActivePackage?.code === pkg.code;
                          const price = pkg.pricing?.[billingInterval] || '0.00';
                          const billedPrice = Number(pkg.charged_pricing?.[billingInterval] ?? 0);
                          const packageCurrency = resolveCatalogItemCurrency(pkg);
                          const includedModules = getPackageIncludedModules(pkg);
                          return (
                            <Grid item xs={12} sm={6} md={3} key={pkg.code} sx={{ display: 'flex' }}>
                              <Box
                                onClick={() => {
                                  setSelectedPackageCode((current) => (current === pkg.code ? '' : pkg.code));
                                }}
                                sx={{
                                  height: '100%',
                                  minHeight: 330,
                                  width: '100%',
                                  cursor: 'pointer',
                                  borderRadius: '18px',
                                  p: 2.2,
                                  border: `1.5px solid ${selected ? '#4ce1b6' : (isDark ? alpha('#fff', 0.12) : alpha('#0f172a', 0.1))}`,
                                  background: selected
                                    ? `linear-gradient(180deg, ${alpha('#4ce1b6', isDark ? 0.16 : 0.12)}, ${isDark ? alpha('#020617', 0.72) : '#ffffff'})`
                                    : (isDark ? 'linear-gradient(180deg, rgba(15,23,42,0.96), rgba(2,6,23,0.94))' : '#ffffff'),
                                  boxShadow: selected ? `0 20px 42px ${alpha('#4ce1b6', 0.2)}` : (isDark ? '0 14px 30px rgba(0,0,0,0.22)' : '0 14px 28px rgba(15,23,42,0.08)'),
                                  transition: 'all 0.2s ease',
                                  display: 'flex',
                                  flexDirection: 'column',
                                }}
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                                  <Box>
                                    <Typography sx={{ fontWeight: 900, color: isDark ? '#f8fafc' : '#0f172a', fontSize: 18 }}>
                                      {pkg.name}
                                    </Typography>
                                    <Typography sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: 13, minHeight: 38 }}>
                                      {pkg.tagline || pkg.description}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                    {isCurrentActivePackage ? <Chip size="small" label="Current" sx={{ bgcolor: alpha('#4ce1b6', 0.16), color: '#0f766e', fontWeight: 800 }} /> : null}
                                    {selected ? <Chip size="small" label={isPackagePaymentLocked && !isCurrentActivePackage ? 'Renewal target' : 'Selected'} sx={{ bgcolor: '#4ce1b6', color: '#06221b', fontWeight: 800 }} /> : null}
                                    {pkg.is_popular ? <Chip size="small" label="Popular" sx={{ bgcolor: alpha('#4ce1b6', 0.16), color: '#0f766e', fontWeight: 800 }} /> : null}
                                  </Box>
                                </Box>
                                <Typography sx={{ fontWeight: 950, color: '#4ce1b6', fontSize: 34, mt: 1 }}>
                                  {formatCurrencyAmount(billedPrice, packageCurrency)}
                                  <Typography component="span" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: 12, ml: 0.5 }}>
                                    total
                                  </Typography>
                                </Typography>
                                <Typography sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: 12 }}>
                                  Final price for {selectedSubscriptionRoomCount} rooms, billed {billingInterval}
                                </Typography>
                                <Typography sx={{ color: isDark ? '#cbd5e1' : '#475569', fontSize: 12, fontWeight: 800, mb: 1.2 }}>
                                  {formatCurrencyAmount(price, packageCurrency)} / month
                                </Typography>
                                <Box sx={{ display: 'grid', gap: 0.5, flex: 1 }}>
                                  {includedModules.map((feature) => {
                                    const FeatureIcon = getCatalogIcon(feature);
                                    return (
                                      <Box key={feature} sx={{ display: 'flex', alignItems: 'center', gap: 0.65, color: isDark ? '#d1d5db' : '#334155' }}>
                                        <FeatureIcon sx={{ fontSize: 14, color: '#4ce1b6' }} />
                                        <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                                          {feature}
                                        </Typography>
                                      </Box>
                                    );
                                  })}
                                </Box>
                                <Button
                                  variant="contained"
                                  fullWidth
                                  sx={{
                                    mt: 1.7,
                                    bgcolor: isPackagePaymentLocked ? alpha('#94a3b8', 0.36) : '#4ce1b6',
                                    color: isPackagePaymentLocked ? (isDark ? '#cbd5e1' : '#475569') : '#06221b',
                                    fontWeight: 900,
                                    borderRadius: '10px',
                                    '&:hover': { bgcolor: isPackagePaymentLocked ? alpha('#94a3b8', 0.36) : '#43d6ad' },
                                  }}
                                >
                                  {isPackagePaymentLocked
                                    ? (isCurrentActivePackage ? 'Current Plan Active' : (selected ? 'Renewal Target' : 'Schedule at Renewal'))
                                    : (selected ? 'Selected' : 'Select Plan')}
                                </Button>
                              </Box>
                            </Grid>
                          );
                        })}
                      </Grid>

                      {availableAddons.length ? (
                        <Box sx={{ mt: 2 }}>
                          <Typography sx={{ color: isDark ? '#ecfeff' : '#0f172a', fontWeight: 900, mb: 1 }}>
                            Optional add-ons
                          </Typography>
                          <Grid container spacing={1}>
                            {availableAddons.map((addon) => {
                              const checked = selectedAddonCodes.includes(addon.code);
                              const AddonIcon = getCatalogIcon(addon.name);
                              const addonIntervalTotal = Number(addon.charged_pricing?.[billingInterval] ?? 0);
                              const addonCurrency = resolveCatalogItemCurrency(addon);
                              return (
                                <Grid item xs={12} sm={6} md={3} key={addon.code} sx={{ display: 'flex' }}>
                                  <Box
                                    onClick={() => setSelectedAddonCodes((prev) => (
                                      checked ? prev.filter((code) => code !== addon.code) : [...prev, addon.code]
                                    ))}
                                    sx={{
                                      cursor: 'pointer',
                                      borderRadius: '14px',
                                      p: 1.25,
                                      width: '100%',
                                      minHeight: 148,
                                      height: '100%',
                                      border: `1px solid ${checked ? '#4ce1b6' : (isDark ? alpha('#fff', 0.1) : alpha('#0f172a', 0.1))}`,
                                      backgroundColor: checked ? alpha('#4ce1b6', 0.1) : (isDark ? alpha('#020617', 0.22) : '#fff'),
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                      <Box
                                        sx={{
                                          width: 34,
                                          height: 34,
                                          borderRadius: '12px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          bgcolor: checked ? alpha('#4ce1b6', 0.22) : (isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.05)),
                                          color: checked ? '#4ce1b6' : (isDark ? '#cbd5e1' : '#64748b'),
                                          flexShrink: 0,
                                        }}
                                      >
                                        <AddonIcon sx={{ fontSize: 19 }} />
                                      </Box>
                                      <Box sx={{ minWidth: 0 }}>
                                        <Typography sx={{ fontWeight: 800, color: isDark ? '#f8fafc' : '#0f172a' }}>{addon.name}</Typography>
                                        <Typography sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: 12 }}>{addon.description}</Typography>
                                        <Typography sx={{ color: '#4ce1b6', fontWeight: 900, mt: 0.75 }}>
                                          + {formatCurrencyAmount(addonIntervalTotal, addonCurrency)}
                                          <Typography component="span" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: 11, ml: 0.5 }}>
                                            / {addon.per_room_pricing ? `${selectedSubscriptionRoomCount} rooms / ` : ''}{billingInterval}
                                          </Typography>
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Box>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </Box>
                      ) : null}

                      <Box
                        sx={{
                          mt: 2,
                          p: 1.5,
                          borderRadius: '14px',
                          bgcolor: isDark ? alpha('#020617', 0.28) : '#ffffff',
                          border: `1px solid ${isDark ? alpha('#4ce1b6', 0.18) : alpha('#0f766e', 0.12)}`,
                          display: 'grid',
                          gap: 1.25,
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                          <Box>
                            <Typography sx={{ color: isDark ? '#f8fafc' : '#0f172a', fontWeight: 900 }}>
                              Estimated total: {formatCurrencyAmount(checkoutDisplayTotal, displayCurrency)}
                              {checkoutQuoteLoading ? (
                                <Typography component="span" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: 12, ml: 1, fontWeight: 700 }}>
                                  updating…
                                </Typography>
                              ) : null}
                            </Typography>
                            {couponApplied && checkoutSavingsMajor > 0 ? (
                              <Typography sx={{ color: '#4ce1b6', fontWeight: 800, fontSize: 12, mt: 0.35 }}>
                                Coupon {couponDetails.code} saves {formatCurrencyAmount(checkoutSavingsMajor, displayCurrency)}
                                {couponDetails.discountPercent ? ` (${couponDetails.discountPercent}% off)` : ''}
                              </Typography>
                            ) : null}
                            <Typography sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: 12 }}>
                              Same website pricing for {selectedSubscriptionRoomCount} rooms, billed {billingInterval}. Taxes are applied by checkout.
                            </Typography>
                            {checkoutQuoteError ? (
                              <Typography sx={{ color: '#ff4861', fontSize: 12, fontWeight: 700, mt: 0.35 }}>
                                {checkoutQuoteError}
                              </Typography>
                            ) : null}
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button
                              variant="contained"
                              disabled={!selectedPackage || paymentProcessing || checkoutQuoteLoading || (isPackagePaymentLocked && selectedAddonCodes.length === 0)}
                              onClick={handleOpenPaymentGateway}
                              sx={{ bgcolor: '#4ce1b6', color: '#06221b', fontWeight: 900, borderRadius: 999, px: 3, '&:hover': { bgcolor: '#43d6ad' } }}
                            >
                              {paymentProcessing ? 'Opening payment...' : (isPackagePaymentLocked ? 'Buy Add-ons' : 'Upgrade Now')}
                            </Button>
                            <Button
                              variant="outlined"
                              disabled={
                                !selectedPackage
                                || !currentActivePackage
                                || currentActivePackage.code === selectedPackage.code
                                || scheduleProcessing
                              }
                              onClick={handleSchedulePackageChange}
                              sx={{ borderColor: alpha('#4ce1b6', 0.55), color: isDark ? '#d1fae5' : '#0f766e', fontWeight: 900, borderRadius: 999, px: 3 }}
                            >
                              {scheduleProcessing ? 'Scheduling...' : 'Schedule Change'}
                            </Button>
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            pt: 1.25,
                            borderTop: `1px solid ${isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.08)}`,
                            display: 'grid',
                            gap: 0.75,
                          }}
                        >
                          {(couponStatus === 'applied' || couponStatus === 'error' || couponMessage) ? (
                            <Typography
                              sx={{
                                fontSize: 12,
                                fontWeight: couponApplied ? 800 : 600,
                                color: couponApplied ? '#4ce1b6' : (couponStatus === 'error' ? '#ff4861' : (isDark ? '#94a3b8' : '#64748b')),
                              }}
                            >
                              {couponMessage}
                            </Typography>
                          ) : null}
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            <TextField
                              size="small"
                              label="Coupon"
                              placeholder="Apply coupon"
                              value={couponInput}
                              onChange={(event) => {
                                const nextValue = event.target.value;
                                setCouponInput(nextValue);
                                setCouponMessage('');
                                if (!nextValue.trim() || couponApplied) {
                                  setCouponStatus('idle');
                                  setCouponDetails(createEmptyCouponDetails());
                                  couponAppliedSignatureRef.current = null;
                                }
                              }}
                              disabled={!paymentPackage?.code || couponApplying}
                              sx={{
                                minWidth: { xs: '100%', sm: 220 },
                                flex: 1,
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '10px',
                                  bgcolor: isDark ? alpha('#020617', 0.45) : '#fff',
                                },
                              }}
                            />
                            <Button
                              variant="outlined"
                              onClick={handleApplyCoupon}
                              disabled={!paymentPackage?.code || couponApplying}
                              sx={{
                                borderColor: alpha('#4ce1b6', 0.55),
                                color: isDark ? '#d1fae5' : '#0f766e',
                                fontWeight: 900,
                                borderRadius: '10px',
                                px: 2.5,
                                height: 40,
                              }}
                            >
                              {couponApplying ? 'Applying…' : 'Apply'}
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    </>
                  )}
                </Box>

                <Box
                  sx={{
                    borderRadius: '14px',
                    border: `1px solid ${isDark ? '#334155' : '#dbe6f0'}`,
                    backgroundColor: isDark ? alpha('#0f172a', 0.32) : '#ffffff',
                    p: { xs: 1.5, sm: 2 },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.6 }}>
                    <Typography sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#0f172a' }}>
                      Hotel Subscription Status
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={hotelsLoading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon sx={{ fontSize: 16 }} />}
                      onClick={() => {
                        if (typeof onRefreshHotels === 'function') onRefreshHotels();
                      }}
                      disabled={hotelsLoading || typeof onRefreshHotels !== 'function'}
                      sx={{
                        ml: 'auto',
                        minHeight: 30,
                        borderRadius: 999,
                        px: 1.4,
                        textTransform: 'none',
                        fontWeight: 800,
                        color: isDark ? '#d1fae5' : '#0f766e',
                        borderColor: alpha('#4ce1b6', 0.45),
                        '&:hover': {
                          borderColor: '#4ce1b6',
                          bgcolor: alpha('#4ce1b6', 0.1),
                        },
                      }}
                    >
                      {hotelsLoading ? 'Refreshing...' : 'Refresh'}
                    </Button>
                  </Box>
                  <Typography sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: 13, mb: 2 }}>
                    Expired hotels are disabled on landing and cannot be opened until entitlement is renewed.
                  </Typography>

                  {hotelsLoading ? (
                    <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={30} />
                    </Box>
                  ) : hotelList.length === 0 ? (
                    <Typography sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                      No hotels available for this user.
                    </Typography>
                  ) : (
                    <Grid container spacing={1.2}>
                      {hotelList.map((hotel) => {
                        const tone = statusTone(hotel.subscription_status, isDark);
                        const packageNames = Array.isArray(hotel.subscription_package_names)
                          ? hotel.subscription_package_names
                          : [];
                        const packageDetails = Array.isArray(hotel.subscription_package_details)
                          ? hotel.subscription_package_details
                          : [];
                        const packageLabels = packageDetails.length
                          ? packageDetails.map((detail) => [
                            `${detail.name} (${detail.billing_interval || 'yearly'})`,
                            Number(detail.room_count || 0) > 0 ? `${detail.room_count} rooms` : '',
                            formatSubscriptionAmount(
                              detail.subscription_amount,
                              Number(detail.subscription_amount || 0) > 0 ? detail.subscription_currency : hotel.currency
                            ),
                          ].filter(Boolean).join(' - '))
                          : packageNames;
                        const addonPermissionCodes = Array.isArray(hotel.subscription_addon_permission_codes)
                          ? hotel.subscription_addon_permission_codes
                          : [];
                        const addonFeatures = Array.isArray(hotel.subscription_addon_features)
                          ? hotel.subscription_addon_features
                          : [];
                        const packageCodes = packageDetails.map((detail) => detail.code).filter(Boolean);
                        const currentPackages = (packageDetails.length ? packageDetails : packageNames)
                          .map((packageRef) => {
                            const packageCode = typeof packageRef === 'string' ? '' : packageRef.code;
                            const packageName = typeof packageRef === 'string' ? packageRef : packageRef.name;
                            return (subscriptionCatalog.packages || []).find((pkg) => (
                              (packageCode && pkg.code === packageCode)
                              || packageCodes.includes(pkg.code)
                              || pkg.internal_name === packageName
                              || pkg.name === packageName
                            ));
                          })
                          .filter(Boolean);
                        const hasHallAddon = addonPermissionCodes.includes('common.manage_banquet_conference')
                          || addonFeatures.some((feature) => {
                            const normalized = String(feature || '').toLowerCase();
                            return (
                              normalized.includes('hall')
                              || normalized.includes('banquet')
                              || normalized.includes('conference')
                            );
                          });
                        const packageFeatureLabels = Array.from(new Set(
                          currentPackages.flatMap((pkg) => pkg.features || []).map(normalizeSubscriptionFeatureLabel)
                        ));
                        const addonFeatureLabels = Array.from(new Set(
                          addonFeatures.map(normalizeSubscriptionFeatureLabel)
                        )).filter(Boolean);
                        const addonFeatureSet = new Set(
                          addonFeatureLabels.map((feature) => String(feature || '').toLowerCase())
                        );
                        const includedFeatures = Array.from(new Set([
                          ...packageFeatureLabels,
                          ...addonFeatureLabels,
                        ])).filter((feature) => {
                          const normalizedFeature = String(feature || '').toLowerCase();
                          // Hall is Hotel Additional Features only — never show from package bullets.
                          const isHallFeature = (
                            normalizedFeature === 'hall'
                            || normalizedFeature.includes('hall booking')
                            || normalizedFeature.includes('banquet')
                            || normalizedFeature.includes('conference')
                          );
                          return !isHallFeature || hasHallAddon;
                        });
                        const hotelKey = String(hotel.id);
                        const showAllFeatures = expandedFeatureHotelIds.has(hotelKey);
                        const visibleIncludedFeatures = showAllFeatures
                          ? includedFeatures
                          : includedFeatures.slice(0, 8);
                        return (
                          <Grid item xs={12} key={hotel.id}>
                            <Box
                              sx={{
                                borderRadius: '12px',
                                border: `1px solid ${isDark ? alpha('#ffffff', 0.12) : alpha('#0f172a', 0.1)}`,
                                backgroundColor: isDark ? '#1e293b' : '#f8fafc',
                                p: { xs: 1.4, sm: 1.7 },
                                display: 'flex',
                                alignItems: { xs: 'flex-start', md: 'center' },
                                justifyContent: 'space-between',
                                flexDirection: { xs: 'column', md: 'row' },
                                gap: 1.2,
                              }}
                            >
                              <Box sx={{ minWidth: 0, width: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.2 }}>
                                  <Typography sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#0f172a' }}>
                                    {hotel.name || `Hotel #${hotel.id}`}
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                                    <Button
                                      size="small"
                                      startIcon={<ReceiptLongIcon sx={{ fontSize: 16 }} />}
                                      onClick={() => setBillingDrawerHotel(hotel)}
                                      sx={{
                                        minHeight: 28,
                                        borderRadius: 999,
                                        px: 1.2,
                                        textTransform: 'none',
                                        fontWeight: 800,
                                        color: '#4ce1b6',
                                        border: `1px solid ${alpha('#4ce1b6', 0.38)}`,
                                      }}
                                    >
                                      Invoice
                                    </Button>
                                    <Chip
                                      icon={tone.icon}
                                      label={hotel.subscription_status_label || 'Not configured'}
                                      size="small"
                                      sx={{
                                        height: 28,
                                        backgroundColor: tone.background,
                                        border: `1px solid ${tone.border}`,
                                        color: tone.color,
                                        fontWeight: 700,
                                        flexShrink: 0,
                                        '& .MuiChip-icon': { color: tone.color },
                                      }}
                                    />
                                  </Box>
                                </Box>
                                <Typography sx={{ fontSize: 12, color: isDark ? '#9aa4b2' : '#64748b' }}>
                                  Packages: {packageLabels.length ? packageLabels.join(', ') : 'Not assigned'}
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: isDark ? '#9aa4b2' : '#64748b' }}>
                                  Expires: {formatDateTime(hotel.subscription_expires_at)}
                                </Typography>
                                {includedFeatures.length > 0 ? (
                                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, gap: 0.75, mt: 1 }}>
                                    {visibleIncludedFeatures.map((feature) => {
                                      const featureLabel = normalizeSubscriptionFeatureLabel(feature);
                                      const FeatureIcon = getCatalogIcon(featureLabel);
                                      const isAddonFeature = addonFeatureSet.has(String(featureLabel || '').toLowerCase());
                                      const accent = isAddonFeature ? '#f59e0b' : '#4ce1b6';
                                      return (
                                        <Box
                                          key={featureLabel}
                                          title={isAddonFeature ? 'Add-on (Hotel Additional Feature)' : 'Included in package'}
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                            p: 0.8,
                                            borderRadius: '12px',
                                            bgcolor: isDark
                                              ? alpha(accent, isAddonFeature ? 0.16 : 0.1)
                                              : (isAddonFeature ? alpha('#f59e0b', 0.12) : '#ffffff'),
                                            border: `1px solid ${isDark ? alpha(accent, 0.28) : alpha(isAddonFeature ? '#d97706' : '#0f172a', isAddonFeature ? 0.28 : 0.08)}`,
                                            minWidth: 0,
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              width: 28,
                                              height: 28,
                                              borderRadius: '10px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              color: accent,
                                              bgcolor: alpha(accent, 0.14),
                                              flexShrink: 0,
                                            }}
                                          >
                                            <FeatureIcon sx={{ fontSize: 17 }} />
                                          </Box>
                                          <Typography noWrap sx={{ color: isDark ? '#e2e8f0' : '#0f172a', fontSize: 12, fontWeight: 800 }}>
                                            {featureLabel}
                                            {isAddonFeature ? ' · Add-on' : ''}
                                          </Typography>
                                        </Box>
                                      );
                                    })}
                                    {includedFeatures.length > 8 ? (
                                      <Chip
                                        label={showAllFeatures ? 'Show less' : `+${includedFeatures.length - 8} more`}
                                        size="small"
                                        onClick={() => {
                                          setExpandedFeatureHotelIds((current) => {
                                            const next = new Set(current);
                                            if (next.has(hotelKey)) next.delete(hotelKey);
                                            else next.add(hotelKey);
                                            return next;
                                          });
                                        }}
                                        sx={{
                                          height: 22,
                                          fontSize: 11,
                                          fontWeight: 800,
                                          cursor: 'pointer',
                                          bgcolor: isDark ? alpha('#ffffff', 0.16) : alpha('#0f172a', 0.08),
                                          color: isDark ? '#e2e8f0' : '#334155',
                                          '&:hover': {
                                            bgcolor: isDark ? alpha('#4ce1b6', 0.2) : alpha('#4ce1b6', 0.16),
                                          },
                                        }}
                                      />
                                    ) : null}
                                  </Box>
                                ) : packageNames.length > 0 ? (
                                  <Typography sx={{ fontSize: 12, color: isDark ? '#94a3b8' : '#64748b', mt: 0.7 }}>
                                    Included feature details are not available for this package catalog entry.
                                  </Typography>
                                ) : null}
                              </Box>
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}
                </Box>
              </Box>
              )}
            </Box>
          )}
        </Box>
      </Card>
      <SubscriptionBillingDrawer
        open={Boolean(billingDrawerHotel)}
        hotel={billingDrawerHotel}
        onClose={() => setBillingDrawerHotel(null)}
      />
      <Dialog
        open={Boolean(paymentSuccessDialog)}
        onClose={() => setPaymentSuccessDialog(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '24px',
            overflow: 'hidden',
            background: isDark
              ? 'linear-gradient(155deg, rgba(15,23,42,0.98), rgba(6,78,59,0.92))'
              : 'linear-gradient(155deg, #ffffff, #ecfdf5)',
            border: `1px solid ${alpha('#4ce1b6', 0.32)}`,
            boxShadow: `0 28px 80px ${alpha('#4ce1b6', 0.22)}`,
          },
        }}
      >
        <DialogContent sx={{ p: { xs: 2.4, sm: 3.2 }, textAlign: 'center' }}>
          <Box
            sx={{
              width: 74,
              height: 74,
              mx: 'auto',
              mb: 1.8,
              borderRadius: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#06221b',
              bgcolor: '#4ce1b6',
              boxShadow: `0 18px 38px ${alpha('#4ce1b6', 0.36)}`,
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 42 }} />
          </Box>
          <Typography sx={{ fontWeight: 950, fontSize: { xs: 22, sm: 28 }, color: isDark ? '#ecfeff' : '#0f172a' }}>
            Payment Successful
          </Typography>
          <Typography sx={{ color: isDark ? '#a7f3d0' : '#0f766e', fontWeight: 800, mt: 0.8 }}>
            {paymentSuccessDialog?.packageName || 'Subscription payment'} is verified and recorded.
          </Typography>
          <Box
            sx={{
              mt: 2.4,
              p: 1.6,
              borderRadius: '16px',
              bgcolor: isDark ? alpha('#020617', 0.28) : '#ffffff',
              border: `1px solid ${isDark ? alpha('#fff', 0.1) : alpha('#0f172a', 0.08)}`,
              textAlign: 'left',
            }}
          >
            <Typography sx={{ color: isDark ? '#cbd5e1' : '#475569', fontSize: 12, fontWeight: 800 }}>
              Razorpay order
            </Typography>
            <Typography sx={{ color: isDark ? '#f8fafc' : '#0f172a', fontWeight: 900, wordBreak: 'break-all' }}>
              {paymentSuccessDialog?.orderId || '-'}
            </Typography>
            <Typography sx={{ color: isDark ? '#cbd5e1' : '#475569', fontSize: 12, fontWeight: 800, mt: 1 }}>
              Paid amount
            </Typography>
            <Typography sx={{ color: '#4ce1b6', fontWeight: 950, fontSize: 20 }}>
              {formatCurrencyAmount((Number(paymentSuccessDialog?.amount || 0) / 100), paymentSuccessDialog?.currency || displayCurrency)}
            </Typography>
            <Typography sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: 12, mt: 0.7 }}>
              GST invoice includes 18% GST and uses the same website invoice template.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2.4, sm: 3.2 }, pb: 2.8, gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            disabled={!paymentSuccessDialog?.invoiceDownloadUrl}
            onClick={() => handleDownloadInvoice(paymentSuccessDialog?.invoiceDownloadUrl)}
            sx={{ bgcolor: '#4ce1b6', color: '#06221b', fontWeight: 900, borderRadius: 999, px: 2.4, '&:hover': { bgcolor: '#43d6ad' } }}
          >
            Download Invoice
          </Button>
          <Button
            variant="outlined"
            onClick={() => setPaymentSuccessDialog(null)}
            sx={{ borderRadius: 999, px: 2.4, fontWeight: 900, borderColor: alpha('#4ce1b6', 0.45), color: isDark ? '#d1fae5' : '#0f766e' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LandingSettings;
